import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  AuthUser,
} from 'aws-amplify/auth';
import api, { userApi, User, setTokens, clearTokens } from '@/lib/api';

interface AuthState {
  // State
  user: User | null;
  cognitoUser: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  needsVerification: boolean;
  verificationEmail: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  confirmRegistration: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  clearError: () => void;
  setNeedsVerification: (needs: boolean, email?: string) => void;
}

/**
 * Authentication Store using Zustand with AWS Cognito
 * 
 * Features:
 * - AWS Cognito authentication via Amplify
 * - Automatic token refresh
 * - Email verification flow
 * - Persistent user session
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      cognitoUser: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      needsVerification: false,
      verificationEmail: null,

      // Login action
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Sign out any existing user first to prevent "already signed in" error
          try {
            await signOut();
          } catch (e) {
            // Ignore signout errors
          }
          
          // Sign in with Cognito
          const { isSignedIn, nextStep } = await signIn({
            username: email.toLowerCase(),
            password,
          });
          
          if (!isSignedIn) {
            if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
              set({ 
                needsVerification: true, 
                verificationEmail: email,
                isLoading: false,
                error: 'Please verify your email address first.',
              });
              return;
            }
            throw new Error(`Sign in requires additional step: ${nextStep?.signInStep}`);
          }
          
          // Get the auth session for tokens
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken?.toString();
          const idToken = session.tokens?.idToken?.toString();
          
          if (!accessToken || !idToken) {
            throw new Error('Failed to get tokens');
          }
          
          // Store tokens for API calls
          // Use ID token as primary (contains email claim) for API auth
          // Access token is kept for refresh purposes
          setTokens(idToken, accessToken);
          
          // Get Cognito user info
          const cognitoUser = await getCurrentUser();
          
          // Fetch user profile from our backend (this will create user if not exists)
          const { data: user } = await userApi.getMe();
          
          set({ 
            user, 
            cognitoUser,
            isAuthenticated: true, 
            isLoading: false,
            error: null,
            needsVerification: false,
            verificationEmail: null,
          });
        } catch (error: any) {
          console.error('Login error:', error);
          
          let message = 'Login failed. Please try again.';
          
          if (error.name === 'UserNotConfirmedException') {
            set({ 
              needsVerification: true, 
              verificationEmail: email,
              isLoading: false,
              error: 'Please verify your email address.',
            });
            return;
          } else if (error.name === 'NotAuthorizedException') {
            message = 'Invalid email or password.';
          } else if (error.name === 'UserNotFoundException') {
            message = 'No account found with this email.';
          } else if (error.message) {
            message = error.message;
          }
          
          set({ 
            user: null, 
            cognitoUser: null,
            isAuthenticated: false, 
            isLoading: false,
            error: message,
          });
          throw new Error(message);
        }
      },

      // Register action
      register: async (email: string, password: string, name?: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Sign up with Cognito
          const { isSignUpComplete, nextStep } = await signUp({
            username: email.toLowerCase(),
            password,
            options: {
              userAttributes: {
                email: email.toLowerCase(),
                name: name || '',
              },
            },
          });
          
          if (!isSignUpComplete && nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
            // Email verification required
            set({ 
              needsVerification: true,
              verificationEmail: email,
              isLoading: false,
              error: null,
            });
            return;
          }
          
          // If sign up is complete (unlikely with email verification), auto-login
          if (isSignUpComplete) {
            await get().login(email, password);
          }
        } catch (error: any) {
          console.error('Registration error:', error);
          
          let message = 'Registration failed. Please try again.';
          
          if (error.name === 'UsernameExistsException') {
            message = 'An account with this email already exists.';
          } else if (error.name === 'InvalidPasswordException') {
            message = 'Password must be at least 8 characters with uppercase, lowercase, and numbers.';
          } else if (error.message) {
            message = error.message;
          }
          
          set({ 
            isLoading: false,
            error: message,
          });
          throw new Error(message);
        }
      },

      // Confirm registration with verification code
      confirmRegistration: async (email: string, code: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await confirmSignUp({
            username: email.toLowerCase(),
            confirmationCode: code,
          });
          
          set({ 
            needsVerification: false,
            verificationEmail: null,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          console.error('Confirmation error:', error);
          
          let message = 'Verification failed. Please try again.';
          
          if (error.name === 'CodeMismatchException') {
            message = 'Invalid verification code.';
          } else if (error.name === 'ExpiredCodeException') {
            message = 'Verification code has expired. Please request a new one.';
          } else if (error.message) {
            message = error.message;
          }
          
          set({ isLoading: false, error: message });
          throw new Error(message);
        }
      },

      // Resend verification code
      resendCode: async (email: string) => {
        try {
          await resendSignUpCode({
            username: email.toLowerCase(),
          });
        } catch (error: any) {
          console.error('Resend code error:', error);
          throw new Error(error.message || 'Failed to resend code');
        }
      },

      // Forgot password - initiate reset
      forgotPassword: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          await resetPassword({
            username: email.toLowerCase(),
          });

          set({
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          console.error('Forgot password error:', error);

          let message = 'Failed to send reset code. Please try again.';

          if (error.name === 'UserNotFoundException') {
            message = 'No account found with this email.';
          } else if (error.name === 'LimitExceededException') {
            message = 'Too many attempts. Please try again later.';
          } else if (error.message) {
            message = error.message;
          }

          set({ isLoading: false, error: message });
          throw new Error(message);
        }
      },

      // Confirm forgot password with code and new password
      confirmForgotPassword: async (email: string, code: string, newPassword: string) => {
        set({ isLoading: true, error: null });

        try {
          await confirmResetPassword({
            username: email.toLowerCase(),
            confirmationCode: code,
            newPassword,
          });

          set({
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          console.error('Confirm reset password error:', error);

          let message = 'Failed to reset password. Please try again.';

          if (error.name === 'CodeMismatchException') {
            message = 'Invalid verification code.';
          } else if (error.name === 'ExpiredCodeException') {
            message = 'Verification code has expired. Please request a new one.';
          } else if (error.name === 'InvalidPasswordException') {
            message = 'Password must be at least 8 characters with uppercase, lowercase, and numbers.';
          } else if (error.message) {
            message = error.message;
          }

          set({ isLoading: false, error: message });
          throw new Error(message);
        }
      },

      // Logout action
      logout: async () => {
        set({ isLoading: true });
        
        try {
          await signOut();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          clearTokens();
          set({ 
            user: null, 
            cognitoUser: null,
            isAuthenticated: false, 
            isLoading: false,
            error: null,
            needsVerification: false,
            verificationEmail: null,
          });
        }
      },

      // Check auth status on app load
      checkAuth: async () => {
        set({ isLoading: true });
        
        try {
          // Check if user is signed in with Cognito
          const cognitoUser = await getCurrentUser();
          
          // Get fresh session/tokens
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken?.toString();
          const idToken = session.tokens?.idToken?.toString();
          
          if (!accessToken || !idToken) {
            throw new Error('No valid session');
          }
          
          // Update stored tokens - use ID token as primary (contains email)
          setTokens(idToken, accessToken);
          
          // Fetch user profile from our backend
          const { data: user } = await userApi.getMe();
          
          set({ 
            user, 
            cognitoUser,
            isAuthenticated: true, 
            isLoading: false,
            error: null,
          });
        } catch (error) {
          // User not authenticated
          clearTokens();
          set({ 
            user: null, 
            cognitoUser: null,
            isAuthenticated: false, 
            isLoading: false,
            error: null,
          });
        }
      },

      // Update user data (local state only)
      updateUser: (data: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...data } });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Set verification state
      setNeedsVerification: (needs: boolean, email?: string) => set({ 
        needsVerification: needs, 
        verificationEmail: email || null 
      }),
    }),
    {
      name: 'dryvox-auth',
      partialize: (state) => ({
        // Only persist user data, not loading/error states
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
